import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { motion, AnimatePresence } from "framer-motion";
import {
  TrendingUp,
  ArrowLeft,
  Calculator,
  CreditCard,
  PieChart,
  BarChart3,
  Receipt,
  BookMarked,
  GraduationCap,
  FileText,
  Target,
  Plane,
  DollarSign,
  Users,
  Sparkles,
  ChevronRight,
  Search,
  Coins,
  Lock,
} from "lucide-react";
import AuthModal from "@/components/AuthModal";
import CompoundCalculator from "@/components/CompoundCalculator";
import MultiDebtCalculator from "@/components/MultiDebtCalculator";
import ExpenseSplitCalculator from "@/components/ExpenseSplitCalculator";
import InvestTodaySurvey from "@/components/InvestTodaySurvey";
import HomepageQuizFlow from "@/components/HomepageQuizFlow";

import ETFQuizContent from "@/components/ETFQuizContent";
import NetWorthCalculator from "@/components/NetWorthCalculator";
import TripCostCalculator from "@/components/TripCostCalculator";
import SalaryExplorer from "@/components/SalaryExplorer";
import SubscriptionTracker from "@/components/SubscriptionTracker";
import StockSimulator from "@/components/StockSimulator";
import DailyMarketUpdate from "@/components/DailyMarketUpdate";
import LearnHub from "@/components/LearnHub";
import FinancialDocumentAnalyzer from "@/components/FinancialDocumentAnalyzer";

// Tool categories and their tools
const toolCategories = {
  "debt-payoff": {
    title: "Debt Payoff",
    emoji: "💳",
    description: "Tools to eliminate debt and track spending",
    color: "text-red-500",
    bgColor: "bg-red-50",
    borderColor: "border-red-200",
    tools: [
      { id: "debt", name: "Multi-Debt Calculator", icon: CreditCard, description: "Compare Snowball vs Avalanche to pay off multiple debts" },
      { id: "subscriptions", name: "Subscription Tracker", icon: Receipt, description: "Find and eliminate hidden recurring charges" },
    ]
  },
  "investing": {
    title: "Investing",
    emoji: "📈",
    description: "Start and grow your investment journey",
    color: "text-emerald-500",
    bgColor: "bg-emerald-50",
    borderColor: "border-emerald-200",
    tools: [
      { id: "simulator", name: "Stock Simulator", icon: TrendingUp, description: "Practice trading with $10K virtual cash" },
      { id: "investor-quiz", name: "Investor Type Quiz", icon: Sparkles, description: "Discover your investing personality" },
      { id: "invest-quiz", name: "Investment Readiness Quiz", icon: Target, description: "Find out if you're ready to start investing" },
      { id: "etf-quiz", name: "Build an ETF Portfolio", icon: BookMarked, description: "Get a personalized ETF portfolio recommendation" },
      { id: "market-update", name: "Daily Market Update", icon: BarChart3, description: "AI-powered market insights and news" },
    ]
  },
  "wealth-building": {
    title: "Wealth Building",
    emoji: "💰",
    description: "Grow and track your net worth",
    color: "text-amber-500",
    bgColor: "bg-amber-50",
    borderColor: "border-amber-200",
    tools: [
      { id: "compound", name: "Compound Interest Calculator", icon: Calculator, description: "See how your money grows over time" },
      { id: "networth", name: "Net Worth Calculator", icon: PieChart, description: "Track your total assets minus liabilities" },
      { id: "salary", name: "Salary Explorer", icon: DollarSign, description: "Research salaries for your field and location" },
    ]
  },
  "budgeting": {
    title: "Budgeting & Planning",
    emoji: "🎯",
    description: "Plan and save for your goals",
    color: "text-purple-500",
    bgColor: "bg-purple-50",
    borderColor: "border-purple-200",
    tools: [
      { id: "trip", name: "Trip Cost Calculator", icon: Plane, description: "Budget for your next vacation or trip" },
      { id: "split", name: "Expense Splitter", icon: Users, description: "Split bills fairly with roommates or friends" },
    ]
  },
  "learning": {
    title: "Learning",
    emoji: "📚",
    description: "Build your financial knowledge",
    color: "text-blue-500",
    bgColor: "bg-blue-50",
    borderColor: "border-blue-200",
    tools: [
      { id: "learnhub", name: "Learn Hub", icon: GraduationCap, description: "105+ financial terms with AI-powered explanations" },
      { id: "document-analyzer", name: "Document Analyzer", icon: FileText, description: "Upload financial docs for AI analysis" },
    ]
  }
};

const Tools = () => {
  const [searchParams] = useSearchParams();
  const toolParam = searchParams.get("tool");
  const fromCategory = searchParams.get("from");
  const modeParam = searchParams.get("mode");
  const [activeTool, setActiveTool] = useState(toolParam || null);
  const [activeCategory, setActiveCategory] = useState("all");
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [showAuth, setShowAuth] = useState(false);

  // Check if user is logged in
  useEffect(() => {
    const userToken = localStorage.getItem("user_token") || localStorage.getItem("token");
    const userData = localStorage.getItem("user_data") || localStorage.getItem("user");
    const hasSubscribed = localStorage.getItem("compoundr_subscribed");
    
    // Check sessionStorage as well
    const sessionToken = sessionStorage.getItem("user_token");
    const sessionUser = sessionStorage.getItem("user_data");
    
    if (userToken || userData || hasSubscribed || sessionToken || sessionUser) {
      setIsUnlocked(true);
      
      // Check if user has pending quiz results (just logged in after taking quiz)
      const pendingQuizResult = localStorage.getItem("pendingInvestorQuizResult");
      if (pendingQuizResult && !activeTool) {
        // Redirect to investor-quiz to show the results
        setActiveTool("investor-quiz");
      }
    }
  }, [activeTool]);

  // Handle auth success
  const handleAuthSuccess = (userData) => {
    localStorage.setItem("user_token", userData.token || "authenticated");
    localStorage.setItem("compoundr_subscribed", "true");
    setIsUnlocked(true);
    setShowAuth(false);
    window.location.reload();
  };

  // Category name mapping
  const categoryNames = {
    "start-investing": "Start Investing",
    "debt-free": "Get Debt-Free",
    "build-wealth": "Build Wealth",
    "learn-finance": "Learn Finance",
    "plan-save": "Plan & Save"
  };

  // Update active tool when URL changes
  useEffect(() => {
    if (toolParam) {
      // Check auth directly here to avoid race conditions
      const userToken = localStorage.getItem("user_token") || localStorage.getItem("token");
      const userData = localStorage.getItem("user_data") || localStorage.getItem("user");
      const hasSubscribed = localStorage.getItem("compoundr_subscribed");
      const sessionToken = sessionStorage.getItem("user_token");
      const sessionUser = sessionStorage.getItem("user_data");
      
      const isUserLoggedIn = !!(userToken || userData || hasSubscribed || sessionToken || sessionUser);
      
      // Special case: investor-quiz with mobile-free mode can be accessed without login
      if (toolParam === "investor-quiz" && modeParam === "mobile-free") {
        setActiveTool(toolParam);
      } else if (isUserLoggedIn || toolParam === "investor-quiz") {
        // If user is logged in OR it's the investor quiz (which has free access)
        setActiveTool(toolParam);
        setIsUnlocked(true); // Also update the state
      } else {
        // Show auth modal for non-free tools
        setShowAuth(true);
      }
    }
  }, [toolParam, modeParam]);

  const handleToolClick = (toolId) => {
    // Check auth directly to avoid stale state issues
    const userToken = localStorage.getItem("user_token") || localStorage.getItem("token");
    const userData = localStorage.getItem("user_data") || localStorage.getItem("user");
    const hasSubscribed = localStorage.getItem("compoundr_subscribed");
    const sessionToken = sessionStorage.getItem("user_token");
    const sessionUser = sessionStorage.getItem("user_data");
    
    const isUserLoggedIn = !!(userToken || userData || hasSubscribed || sessionToken || sessionUser);
    
    if (!isUserLoggedIn) {
      setShowAuth(true); // Show auth modal if not logged in
      return;
    }
    setActiveTool(toolId);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBackToTools = () => {
    setActiveTool(null);
    window.history.pushState({}, '', '/tools');
  };

  // Render the active tool component
  const renderActiveTool = () => {
    switch (activeTool) {
      case "compound":
        return (
          <div className="space-y-6">
            <Tabs defaultValue="compound" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6" data-testid="calculator-tabs">
                <TabsTrigger value="compound" data-testid="tab-compound">Compound Interest</TabsTrigger>
                <TabsTrigger value="debt" data-testid="tab-debt">Debt-Free Calculator</TabsTrigger>
              </TabsList>
              <TabsContent value="compound">
                <CompoundCalculator />
              </TabsContent>
              <TabsContent value="debt">
                <MultiDebtCalculator />
              </TabsContent>
            </Tabs>
          </div>
        );
      case "debt":
        return <MultiDebtCalculator />;
      case "networth":
        return <NetWorthCalculator />;
      case "trip":
        return <TripCostCalculator />;
      case "split":
        return <ExpenseSplitCalculator />;
      case "salary":
        return <SalaryExplorer />;
      case "subscriptions":
        return <SubscriptionTracker />;
      case "simulator":
        return <StockSimulator onBack={handleBackToTools} hideInternalBackButton={true} />;
      case "market-update":
        return <DailyMarketUpdate onBack={handleBackToTools} hideInternalBackButton={true} />;
      case "learnhub":
        return <LearnHub onBack={handleBackToTools} hideInternalBackButton={true} />;
      case "document-analyzer":
        return <FinancialDocumentAnalyzer />;
      case "invest-quiz":
        return <InvestTodaySurvey onComplete={() => handleBackToTools()} />;
      case "investor-quiz":
        // The investor type quiz - can be taken free on mobile (mode=mobile-free)
        // mobileFreeMode=true means user came from mobile and must sign in to see results
        return (
          <div className="py-8">
            <HomepageQuizFlow 
              mobileFreeMode={modeParam === "mobile-free"}
              onRequireLogin={() => setShowAuth(true)}
              onComplete={() => handleBackToTools()}
            />
          </div>
        );
      case "etf-quiz":
        return <ETFQuizContent onComplete={() => handleBackToTools()} />;
      default:
        return null;
    }
  };

  // Get all tools as flat array
  const getAllTools = () => {
    const allTools = [];
    Object.entries(toolCategories).forEach(([categoryId, category]) => {
      category.tools.forEach(tool => {
        allTools.push({ ...tool, categoryId, categoryTitle: category.title, categoryColor: category.color });
      });
    });
    return allTools;
  };

  // Filter tools by category
  const getFilteredTools = () => {
    if (activeCategory === "all") return getAllTools();
    return toolCategories[activeCategory]?.tools.map(tool => ({
      ...tool,
      categoryId: activeCategory,
      categoryTitle: toolCategories[activeCategory].title,
      categoryColor: toolCategories[activeCategory].color
    })) || [];
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50">
      {/* Sign In Banner for Non-Logged-In Users */}
      {!isUnlocked && !activeTool && (
        <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white py-3 px-4 text-center">
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-3">
            <Lock className="w-4 h-4" />
            <span className="font-medium">Sign in to unlock all financial tools and start your journey</span>
            <Button 
              onClick={() => setShowAuth(true)}
              className="bg-white text-emerald-700 hover:bg-emerald-50 px-6 py-1 text-sm font-semibold"
            >
              Sign In Free
            </Button>
          </div>
        </div>
      )}
      
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pt-24">
        {activeTool ? (
          // Show active tool
          <div>
            {/* Back to All Tools - always shows */}
            <Button
              variant="ghost"
              onClick={handleBackToTools}
              className="mb-2 text-slate-600 hover:text-emerald-600"
              data-testid="back-to-tools-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to All Tools
            </Button>
            
            {/* Back to Category - shows if came from a category page */}
            {fromCategory && categoryNames[fromCategory] && (
              <Link to={`/${fromCategory}`}>
                <Button
                  variant="ghost"
                  className="mb-6 text-emerald-600 hover:text-emerald-700 font-medium"
                  data-testid="back-to-category-btn"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to {categoryNames[fromCategory]}
                </Button>
              </Link>
            )}
            
            {renderActiveTool()}
          </div>
        ) : (
          // Show tools grid
          <div>
            {/* Back to Home */}
            <Link to="/" className="inline-flex items-center text-emerald-600 hover:text-emerald-700 mb-4 text-sm font-medium">
              ← Back to Home
            </Link>
            
            {/* Page Header */}
            <div className="text-center mb-10">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <Badge className="bg-emerald-100 text-emerald-700 mb-4">
                  <Sparkles className="w-3 h-3 mr-1" />
                  All Tools
                </Badge>
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-navy-700 mb-4">
                  Explore All Financial Tools
                </h1>
                <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                  Not sure where to start? Browse all our tools organized by category. 
                  Pick any tool to begin your financial journey.
                </p>
              </motion.div>
            </div>

            {/* Category Filter */}
            <div className="flex flex-wrap justify-center gap-2 mb-8">
              <Button
                variant={activeCategory === "all" ? "default" : "outline"}
                onClick={() => setActiveCategory("all")}
                className={activeCategory === "all" ? "bg-emerald-500 hover:bg-emerald-600" : ""}
                data-testid="filter-all"
              >
                All Tools
              </Button>
              {Object.entries(toolCategories).map(([id, category]) => (
                <Button
                  key={id}
                  variant={activeCategory === id ? "default" : "outline"}
                  onClick={() => setActiveCategory(id)}
                  className={activeCategory === id ? "bg-emerald-500 hover:bg-emerald-600" : ""}
                  data-testid={`filter-${id}`}
                >
                  {category.emoji} {category.title}
                </Button>
              ))}
            </div>

            {/* Tools by Category */}
            {activeCategory === "all" ? (
              // Show all categories
              Object.entries(toolCategories).map(([categoryId, category]) => (
                <motion.div
                  key={categoryId}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-10"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-2xl">{category.emoji}</span>
                    <h2 className={`text-xl font-heading font-bold ${category.color}`}>
                      {category.title}
                    </h2>
                    <span className="text-slate-400 text-sm">{category.description}</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {category.tools.map((tool) => (
                      <motion.div
                        key={tool.id}
                        whileHover={isUnlocked ? { scale: 1.02, y: -4 } : {}}
                        whileTap={isUnlocked ? { scale: 0.98 } : {}}
                      >
                        <Card
                          className={`${isUnlocked ? 'cursor-pointer hover:shadow-lg' : 'cursor-not-allowed opacity-75'} transition-all duration-300 ${category.bgColor} ${category.borderColor} border-2`}
                          onClick={() => handleToolClick(tool.id)}
                          data-testid={`tool-card-${tool.id}`}
                        >
                          <CardContent className="p-5">
                            <div className="flex items-start gap-4">
                              <div className={`w-12 h-12 rounded-xl ${category.bgColor} flex items-center justify-center`}>
                                <tool.icon className={`w-6 h-6 ${category.color}`} />
                              </div>
                              <div className="flex-1">
                                <h3 className="font-semibold text-navy-700 mb-1">{tool.name}</h3>
                                <p className="text-sm text-slate-500">{tool.description}</p>
                              </div>
                              {isUnlocked ? (
                                <ChevronRight className="w-5 h-5 text-slate-400" />
                              ) : (
                                <Lock className="w-5 h-5 text-slate-400" />
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              ))
            ) : (
              // Show filtered category
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {getFilteredTools().map((tool) => (
                    <motion.div
                      key={tool.id}
                      whileHover={isUnlocked ? { scale: 1.02, y: -4 } : {}}
                      whileTap={isUnlocked ? { scale: 0.98 } : {}}
                    >
                      <Card
                        className={`${isUnlocked ? 'cursor-pointer hover:shadow-lg' : 'cursor-not-allowed opacity-75'} transition-all duration-300 ${toolCategories[tool.categoryId].bgColor} ${toolCategories[tool.categoryId].borderColor} border-2`}
                        onClick={() => handleToolClick(tool.id)}
                        data-testid={`tool-card-${tool.id}`}
                      >
                        <CardContent className="p-5">
                          <div className="flex items-start gap-4">
                            <div className={`w-12 h-12 rounded-xl ${toolCategories[tool.categoryId].bgColor} flex items-center justify-center`}>
                              <tool.icon className={`w-6 h-6 ${tool.categoryColor}`} />
                            </div>
                            <div className="flex-1">
                              <h3 className="font-semibold text-navy-700 mb-1">{tool.name}</h3>
                              <p className="text-sm text-slate-500">{tool.description}</p>
                            </div>
                            {isUnlocked ? (
                              <ChevronRight className="w-5 h-5 text-slate-400" />
                            ) : (
                              <Lock className="w-5 h-5 text-slate-400" />
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Still confused CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-12 text-center p-8 bg-gradient-to-br from-navy-700 to-navy-800 rounded-3xl"
            >
              <span className="text-3xl mb-4 block">🤔</span>
              <h3 className="text-xl font-heading font-bold text-white mb-2">
                Still not sure where to start?
              </h3>
              <p className="text-slate-300 mb-6 max-w-md mx-auto">
                Take our quick quiz to understand your financial goals and get personalized recommendations.
              </p>
              <Link to="/category-quiz">
                <Button className="bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-6 text-lg rounded-xl">
                  <Target className="w-5 h-5 mr-2" />
                  Take the Quiz to Find Your Path
                </Button>
              </Link>
            </motion.div>
          </div>
        )}
      </div>

      {/* Chatbot */}
      

      {/* Auth Modal for locked tools */}
      <AuthModal
        isOpen={showAuth}
        onClose={() => setShowAuth(false)}
        onSuccess={handleAuthSuccess}
        required={true}
      />
    </div>
  );
};

export default Tools;
